import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import Login from './components/auth/Login';
import OTPVerification from './components/auth/OTPVerification';
import TicketList from './components/tickets/TicketList';
import TicketDetail from './components/tickets/TicketDetail';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/" replace />;
};

// Auth Route Component (redirects to tickets if already authenticated)
const AuthRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/tickets" replace /> : children;
};

function App() {
  const [authStep, setAuthStep] = useState('login'); // 'login' or 'otp'
  const [email, setEmail] = useState('');

  const handleOtpSent = (userEmail) => {
    setEmail(userEmail);
    setAuthStep('otp');
  };

  const handleBackToLogin = () => {
    setAuthStep('login');
    setEmail('');
  };

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10B981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#fff',
                },
              },
            }}
          />

          <Routes>
            {/* Auth Routes */}
            <Route
              path="/"
              element={
                <AuthRoute>
                  {authStep === 'login' ? (
                    <Login onOtpSent={handleOtpSent} />
                  ) : (
                    <OTPVerification email={email} onBack={handleBackToLogin} />
                  )}
                </AuthRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/tickets"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TicketList />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/tickets/:uuid"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TicketDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
